import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Globe, 
  DollarSign, 
  Package, 
  Truck,
  ArrowRight,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Accurate, export-quality images
const IMAGES = {
  // Product Categories - 4 categories
  rawCotton: "https://images.pexels.com/photos/35081362/pexels-photo-35081362.jpeg?auto=compress&w=800",
  dehydrated: "https://images.pexels.com/photos/26823080/pexels-photo-26823080.jpeg?auto=compress&w=800",
  seeds: "https://images.pexels.com/photos/7420982/pexels-photo-7420982.jpeg?auto=compress&w=800",
  nuts: "https://images.pexels.com/photos/7051132/pexels-photo-7051132.jpeg?auto=compress&w=800",
  // Quality section
  quality: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80",
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Hero Section - Premium Agricultural Brand Visual
const HeroSection = () => {
  return (
    <section 
      className="relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #F7F4EA 0%, #DCE8D8 100%)' }}
      data-testid="hero-section"
    >
      {/* Subtle world map background */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <svg viewBox="0 0 1200 600" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <g fill="#1D542E">
            {Array.from({ length: 40 }).map((_, i) => (
              Array.from({ length: 20 }).map((_, j) => {
                const cx = 30 + i * 30;
                const cy = 30 + j * 30;
                const inContinent = 
                  (i > 5 && i < 12 && j > 3 && j < 10) ||
                  (i > 14 && i < 22 && j > 4 && j < 11) ||
                  (i > 22 && i < 35 && j > 3 && j < 12);
                if (inContinent) {
                  return <circle key={`${i}-${j}`} cx={cx} cy={cy} r="2" />;
                }
                return null;
              })
            ))}
          </g>
        </svg>
      </div>

      {/* Faded agricultural leaf accents */}
      <div className="absolute top-10 left-10 w-32 h-32 opacity-[0.15] pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 100 100" fill="#4F7D4A">
          <path d="M50 10 Q60 30 55 50 Q50 70 50 90 Q50 70 45 50 Q40 30 50 10 Z" />
        </svg>
      </div>
      <div className="absolute top-32 right-16 w-40 h-40 opacity-[0.15] pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 100 100" fill="#1D542E">
          <circle cx="50" cy="50" r="8" />
          <circle cx="30" cy="35" r="6" />
          <circle cx="70" cy="35" r="6" />
          <circle cx="35" cy="65" r="7" />
          <circle cx="65" cy="65" r="7" />
        </svg>
      </div>
      <div className="absolute bottom-16 left-1/4 w-24 h-24 opacity-[0.12] pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 100 100" fill="#4F7D4A">
          <path d="M50 5 Q65 25 60 50 Q55 75 50 95 Q45 75 40 50 Q35 25 50 5 Z" />
        </svg>
      </div>

      <div className="container-custom relative z-10 pt-24 pb-24 lg:pt-32 lg:pb-32">
        {/* Top Brand Section */}
        <motion.div 
          className="text-center max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Brand Mark */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-3 mb-3">
              <div className="h-px w-12 bg-secondary/40" />
              <span className="text-xs tracking-[0.35em] uppercase text-secondary font-medium">
                Est. Nirmal Exports
              </span>
              <div className="h-px w-12 bg-secondary/40" />
            </div>
            <h2 className="font-heading font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-primary mb-2">
              NIRMAL <span className="font-black">EXPORTS</span>
            </h2>
            <div className="flex items-center justify-center gap-2 mt-4">
              <span className="text-secondary text-lg">✦</span>
              <p className="font-heading italic text-lg sm:text-xl lg:text-2xl text-primary/80 font-light tracking-wide">
                From Our Fields to the World
              </p>
              <span className="text-secondary text-lg">✦</span>
            </div>
          </motion.div>

          {/* Subheadline */}
          <motion.p 
            className="text-base sm:text-lg text-charcoal/80 max-w-2xl mx-auto leading-relaxed mb-10 font-body"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Premium Indian-origin raw cotton, dehydrated onion &amp; garlic, seeds and nuts —
            curated for discerning global buyers across four continents.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-wrap gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Link to="/products">
              <Button 
                size="lg" 
                className="btn-primary font-semibold px-8 py-6 text-base shadow-lg"
                data-testid="hero-view-products-btn"
              >
                Explore Our Harvest
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="btn-outline font-semibold px-8 py-6 text-base"
                data-testid="hero-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </motion.div>

          {/* Bottom brand values */}
          <motion.div 
            className="mt-14"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <div className="inline-flex flex-wrap items-center justify-center gap-3 text-primary/60 text-sm font-body">
              <span className="text-secondary">●</span>
              <span className="tracking-widest uppercase text-xs font-medium">Natural</span>
              <span className="text-secondary/40">·</span>
              <span className="tracking-widest uppercase text-xs font-medium">Premium</span>
              <span className="text-secondary/40">·</span>
              <span className="tracking-widest uppercase text-xs font-medium">Sustainable</span>
              <span className="text-secondary/40">·</span>
              <span className="tracking-widest uppercase text-xs font-medium">Global</span>
              <span className="text-secondary">●</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Shield,
      title: 'Premium Quality Assurance',
      description: 'Rigorous quality checks at every stage ensure only the finest products reach our customers.'
    },
    {
      icon: Truck,
      title: 'Reliable Global Shipping',
      description: 'Efficient logistics network ensuring timely delivery to ports worldwide.'
    },
    {
      icon: DollarSign,
      title: 'Competitive Pricing',
      description: 'Direct sourcing from farms enables us to offer the best market prices.'
    },
    {
      icon: Package,
      title: 'Consistent Bulk Supply',
      description: 'Strong supply chain guarantees consistent availability for large orders.'
    },
    {
      icon: Globe,
      title: 'Export-Grade Packaging',
      description: 'International standard packaging that meets all export regulations.'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-surface" data-testid="why-choose-section">
      <div className="container-custom">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          {...fadeInUp}
          viewport={{ once: true }}
        >
          <span className="text-secondary font-medium text-sm tracking-wider uppercase">Our Advantages</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mt-3 mb-4">
            Why Choose Nirmal Exports?
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            Your trusted partner for premium agricultural exports from India
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={fadeInUp}
              className={index === 4 ? 'md:col-span-2 lg:col-span-1 lg:mx-auto lg:max-w-sm' : ''}
            >
              <Card className="feature-card h-full border-2 border-transparent hover:border-secondary/20 bg-white shadow-sm">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-primary mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Products Showcase Section - 4 categories
const ProductsSection = () => {
  const products = [
    {
      name: 'Raw Cotton',
      image: IMAGES.rawCotton,
      description: 'Premium cotton yarn, cotton waste, cotton noil, and lickerin for textile industry.',
      link: '/products#raw-cotton'
    },
    {
      name: 'Dehydrated Onion & Garlic',
      image: IMAGES.dehydrated,
      description: 'Export-quality dehydrated onion and garlic in flakes, granules, powder, and more.',
      link: '/products#dehydrated'
    },
    {
      name: 'Seeds',
      image: IMAGES.seeds,
      description: 'High-grade sesame, chia, basil, mustard, sunflower, and pumpkin seeds.',
      link: '/products#seeds'
    },
    {
      name: 'Nuts',
      image: IMAGES.nuts,
      description: 'Premium quality fox nuts (Makhana) sourced from Indian farms.',
      link: '/products#nuts'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-ivory" data-testid="products-section">
      <div className="container-custom">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          {...fadeInUp}
          viewport={{ once: true }}
        >
          <span className="text-secondary font-medium text-sm tracking-wider uppercase">Our Products</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mt-3 mb-4">
            Export-Grade Product Categories
          </h2>
          <p className="text-charcoal/70 text-base sm:text-lg">
            Premium agricultural products meeting international quality standards
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {products.map((product) => (
            <motion.div
              key={product.name}
              variants={fadeInUp}
            >
              <Link to={product.link}>
                <Card className="product-card group overflow-hidden border-0 shadow-lg h-full">
                  <div className="relative h-64 img-container overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-heading font-bold text-xl text-white mb-2">
                        {product.name}
                      </h3>
                      <p className="text-white/80 text-sm mb-4">
                        {product.description}
                      </p>
                      <span className="inline-flex items-center text-white text-sm font-medium group-hover:gap-3 transition-all">
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="text-center mt-12"
          {...fadeInUp}
          viewport={{ once: true }}
        >
          <Link to="/products">
            <Button 
              size="lg" 
              className="btn-primary font-semibold px-8"
              data-testid="view-all-products-btn"
            >
              View All Products
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Global Presence Section
const GlobalPresenceSection = () => {
  const regions = [
    { name: 'Asia', countries: 'China, Japan, South Korea, Vietnam, Thailand' },
    { name: 'Middle East', countries: 'UAE, Saudi Arabia, Qatar, Oman, Kuwait' },
    { name: 'Africa', countries: 'Egypt, South Africa, Kenya, Nigeria, Morocco' },
    { name: 'Europe', countries: 'Germany, UK, France, Italy, Netherlands' }
  ];

  return (
    <section className="py-20 lg:py-28 bg-primary text-white" data-testid="global-presence-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-accent font-medium text-sm tracking-wider uppercase">Global Reach</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-3 mb-6">
              Serving Buyers Across 4 Continents
            </h2>
            <p className="text-white/70 text-lg mb-8 leading-relaxed">
              Our robust export network spans across Asia, Middle East, Africa, and Europe. 
              We understand international trade requirements and ensure seamless delivery to your destination.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              {regions.map((region) => (
                <div key={region.name} className="border-l-2 border-accent pl-4">
                  <h4 className="font-heading font-semibold text-lg mb-1">{region.name}</h4>
                  <p className="text-white/60 text-sm">{region.countries}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* World Map SVG placeholder */}
            <div className="relative bg-white/5 rounded-2xl p-8 backdrop-blur-sm border border-white/10">
              <svg viewBox="0 0 800 400" className="w-full h-auto opacity-30">
                <path
                  d="M100,200 Q200,100 300,150 T500,120 T700,180"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-white"
                />
                <path
                  d="M150,250 Q250,300 350,280 T550,300 T750,250"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-white"
                />
              </svg>
              
              {/* Animated dots representing export destinations */}
              <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-accent rounded-full map-dot" />
              <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-secondary rounded-full map-dot" style={{ animationDelay: '0.5s' }} />
              <div className="absolute bottom-1/3 left-1/2 w-3 h-3 bg-accent rounded-full map-dot" style={{ animationDelay: '1s' }} />
              <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-secondary rounded-full map-dot" style={{ animationDelay: '1.5s' }} />
              
              {/* Stats overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="font-heading font-bold text-5xl sm:text-6xl text-accent mb-2">25+</div>
                  <div className="text-white/80 text-lg">Countries Served</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Quality Assurance Section
const QualitySection = () => {
  const qualityPoints = [
    'Strict quality checks at source and warehouse',
    'Proper moisture and grading control',
    'Export-safe packaging standards',
    'Documentation compliance for international trade',
    'Traceability from farm to shipment'
  ];

  return (
    <section className="py-20 lg:py-28" data-testid="quality-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={IMAGES.quality}
                alt="Quality Control"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                    <div className="font-heading font-bold text-3xl text-white">100%</div>
                    <div className="text-white/80 text-sm">Quality Checked</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                    <div className="font-heading font-bold text-3xl text-white">500+</div>
                    <div className="text-white/80 text-sm">Happy Clients</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <span className="text-secondary font-medium text-sm tracking-wider uppercase">Quality First</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mt-3 mb-6">
              Uncompromising Quality Standards
            </h2>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              At Nirmal Exports, quality isn't just a promise—it's our foundation. 
              Every product undergoes rigorous inspection to meet international export standards.
            </p>
            
            <ul className="space-y-4 mb-8">
              {qualityPoints.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                  <span className="text-foreground">{point}</span>
                </li>
              ))}
            </ul>

            <Link to="/quality">
              <Button 
                className="btn-secondary text-white font-semibold"
                data-testid="learn-quality-btn"
              >
                Learn About Our Quality Process
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-20 bg-primary" data-testid="cta-section">
      <div className="container-custom">
        <motion.div 
          className="text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
            Ready to Start Your Export Order?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join hundreds of satisfied buyers worldwide. Get competitive quotes for bulk orders today.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-sage font-semibold px-8 py-6 text-base shadow-lg"
              data-testid="cta-quote-btn"
            >
              Request Bulk Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Main HomePage Component
const HomePage = () => {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <WhyChooseUsSection />
      <ProductsSection />
      <GlobalPresenceSection />
      <QualitySection />
      <CTASection />
    </div>
  );
};

export default HomePage;
